import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";
import {
  IconButton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import PlaylistAdd from "@mui/icons-material/PlaylistAdd";
import {
  ArrowForwardIos,
  Check,
  DeleteOutline,
  EditOutlined,
  EjectOutlined,
} from "@mui/icons-material";
import { useMemo, useState } from "react";
import StyledTable from "components/StyledTable";
import { useSortedData } from "../../../../lib/hooks/useSortedData";
import TableRowAction from "components/TableRowAction";
import { Product } from "models/Product";
import TableRowMenu from "components/TableRowMenu";
import SwapHoriz from "@mui/icons-material/SwapHoriz";

type ProductAction = "edit" | "add-child" | "delete" | "move" | "uninstall";
export type ActionHandler = (serial: Product, action: ProductAction) => void;

type Props = {
  serials: Product[];
  onAction?: ActionHandler;
};

const ProductsTable: React.FC<Props> = ({ onAction, serials }) => {
  const { hasRole } = useAuthContext();
  const userCanEditProducts = hasRole([
    UserRole.ADMIN,
    UserRole.USER_ADMIN,
    UserRole.SALES,
    UserRole.INTERNAL_INSTALLER,
    UserRole.EXTERNAL_INSTALLER,
  ]);

  const {
    data: sortedSerials,
    sortBy,
    sortDir,
    setSortBy,
  } = useSortedData(serials, [
    { key: "item" },
    { key: "description" },
    { key: "serial", defaultSortDir: "desc" },
    { key: "existing" },
  ]);

  const [isSerialsExpanded, setIsSerialsExpanded] = useState<Map<string, boolean>>(new Map());

  const expandedSerials = useMemo(() => {
    return sortedSerials.flatMap((s) =>
      isSerialsExpanded.get(s.id) ? [s, ...s.children] : [s]
    ) as Product[];
  }, [isSerialsExpanded, sortedSerials]);

  const handleToggleExpand = (id: string) => {
    setIsSerialsExpanded((prev) => {
      const newMap = new Map(prev);
      newMap.set(id, !newMap.get(id));
      return newMap;
    });
  };

  return (
    <TableContainer>
      <StyledTable size="small" striped>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>

            <TableCell>
              <TableSortLabel
                active={sortBy === "item"}
                direction={sortDir}
                onClick={() => setSortBy("item")}
              >
                Artikelkod
              </TableSortLabel>
            </TableCell>

            <TableCell>
              <TableSortLabel
                active={sortBy === "description"}
                direction={sortDir}
                onClick={() => setSortBy("description")}
              >
                Benämning
              </TableSortLabel>
            </TableCell>

            <TableCell>
              <TableSortLabel
                active={sortBy === "serial"}
                direction={sortDir}
                onClick={() => setSortBy("serial")}
              >
                Serienummer
              </TableSortLabel>
            </TableCell>

            <TableCell align="right">
              <TableSortLabel
                active={sortBy === "existing"}
                direction={sortDir}
                onClick={() => setSortBy("existing")}
              >
                Befintlig
              </TableSortLabel>
            </TableCell>

            {userCanEditProducts && <TableCell align="right">Åtgärder</TableCell>}
          </TableRow>
        </TableHead>

        <TableBody>
          {expandedSerials.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ fontStyle: "italic" }}>
                Inga data att visa
              </TableCell>
            </TableRow>
          )}

          {expandedSerials.map((row) => {
            const isChild = !!row.parentId;
            const hasChildren = row.children.length > 0;
            const isExpanded = isSerialsExpanded.get(row.id);

            return (
              <TableRow key={row.id}>
                <TableCell sx={{ pr: 0 }}>
                  <IconButton
                    size="small"
                    edge="start"
                    hidden={!hasChildren}
                    sx={{ my: -0.5, mx: -1 }}
                    onClick={() => handleToggleExpand(row.id)}
                  >
                    <ArrowForwardIos
                      sx={{
                        fontSize: "1em",
                        transition: "transform 0.2s",
                        ...(isExpanded && { transform: "rotate(90deg)" }),
                      }}
                    />
                  </IconButton>
                </TableCell>

                {!isChild ? (
                  <>
                    <TableCell sx={{ textWrap: "nowrap" }}>{row.item}</TableCell>
                    <TableCell sx={{ width: "100%" }}>{row.description}</TableCell>
                  </>
                ) : (
                  <TableCell colSpan={2} sx={{ width: "100%" }}>
                    <Typography component="span" sx={{ mr: 2, opacity: 0.5 }}>
                      &#x2022;
                    </Typography>
                    {row.description}
                  </TableCell>
                )}

                <TableCell sx={{ textWrap: "nowrap" }}>{row.serial}</TableCell>

                <TableCell align="right">{row.existing ? <Check /> : null}</TableCell>

                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {userCanEditProducts && (
                    <TableRowMenu>
                      <TableRowAction
                        icon={<EditOutlined />}
                        text="Redigera"
                        onClick={() => onAction?.(row, "edit")}
                      />

                      <TableRowAction
                        disabled={isChild || row.existing}
                        icon={<PlaylistAdd />}
                        text="Ny underprodukt"
                        onClick={() => onAction?.(row, "add-child")}
                      />

                      <TableRowAction
                        disabled={isChild}
                        icon={<SwapHoriz />}
                        text="Flytta"
                        onClick={() => onAction?.(row, "move")}
                      />

                      <TableRowAction
                        disabled={isChild || row.existing}
                        icon={<EjectOutlined />}
                        text="Avinstallera"
                        onClick={() => onAction?.(row, "uninstall")}
                      />

                      <TableRowAction
                        disabled={!row.editable}
                        icon={<DeleteOutline />}
                        text="Ta bort"
                        onClick={() => onAction?.(row, "delete")}
                      />
                    </TableRowMenu>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
};

export default ProductsTable;
